import {
	SERVER_TIMING_MEASURES,
	WATERFALL_INITIAL_MEASURES,
	WaterfallMeasures,
} from '@confluence/action-measures/entry-points/WaterfallMeasures.ts';

import { getPerformanceEntries } from './collectFromPerfMarkers.ts';

/**
 * Collects navigation timing waterfall measures for the initial page load.
 *
 * Uses the Navigation Timing API (`PerformanceNavigationTiming`) to record
 * redirection, DNS, TCP, TLS, response, TTFB, full HTML, and server-timing
 * durations as waterfall measures.
 *
 * Extracted from `startBrowserMetricsPageLoad` so it can also be called from
 * `additionalPayloadData` in `@confluence/react-ufo` when BM3 deprecation is
 * enabled.
 */
export function collectInitialLoadWaterfallTimings(): void {
	const t = window.performance?.getEntriesByType?.('navigation')?.[0] as
		| PerformanceNavigationTiming
		| undefined;
	if (!t) {
		return;
	}

	// Redirection time
	if (t.redirectStart >= 0 && t.redirectEnd >= t.redirectStart) {
		WaterfallMeasures.markMeasureStart(WATERFALL_INITIAL_MEASURES.REDIRECTION, t.redirectStart);
		WaterfallMeasures.markMeasureEnd(WATERFALL_INITIAL_MEASURES.REDIRECTION, t.redirectEnd);
	}

	// DNS resolution
	if (t.domainLookupStart >= 0 && t.domainLookupEnd >= t.domainLookupStart) {
		WaterfallMeasures.markMeasureStart(WATERFALL_INITIAL_MEASURES.DNS, t.domainLookupStart);
		WaterfallMeasures.markMeasureEnd(WATERFALL_INITIAL_MEASURES.DNS, t.domainLookupEnd);
	}

	// TCP connection
	if (t.connectStart >= 0 && t.connectEnd >= t.connectStart) {
		WaterfallMeasures.markMeasureStart(WATERFALL_INITIAL_MEASURES.TCP, t.connectStart);
		WaterfallMeasures.markMeasureEnd(WATERFALL_INITIAL_MEASURES.TCP, t.connectEnd);
	}

	// TLS handshake
	if (t.secureConnectionStart >= 0 && t.connectEnd >= 0) {
		WaterfallMeasures.markMeasureStart(WATERFALL_INITIAL_MEASURES.TLS, t.secureConnectionStart);
		WaterfallMeasures.markMeasureEnd(WATERFALL_INITIAL_MEASURES.TLS, t.connectEnd);
	}

	// Response
	if (t.requestStart >= 0 && t.responseStart >= t.requestStart) {
		WaterfallMeasures.markMeasureStart(WATERFALL_INITIAL_MEASURES.RESPONSE, t.requestStart);
		WaterfallMeasures.markMeasureEnd(WATERFALL_INITIAL_MEASURES.RESPONSE, t.responseStart);
	}

	// TTFB
	if (t.startTime >= 0 && t.responseStart >= 0) {
		WaterfallMeasures.markMeasureStart(WATERFALL_INITIAL_MEASURES.TTFB, t.startTime);
		WaterfallMeasures.markMeasureEnd(WATERFALL_INITIAL_MEASURES.TTFB, t.responseStart);
	}

	// Downloading HTML
	if (t.responseStart >= 0 && t.responseEnd >= t.responseStart) {
		WaterfallMeasures.markMeasureStart(WATERFALL_INITIAL_MEASURES.HTML_DOWNLOAD, t.responseStart);
		WaterfallMeasures.markMeasureEnd(WATERFALL_INITIAL_MEASURES.HTML_DOWNLOAD, t.responseEnd);
	}

	// DOMContentLoaded event
	if (
		t.domContentLoadedEventStart >= 0 &&
		t.domContentLoadedEventEnd >= t.domContentLoadedEventStart
	) {
		WaterfallMeasures.markMeasureStart(
			WATERFALL_INITIAL_MEASURES.DOM_CONTENT_LOADED_EVENT,
			t.domContentLoadedEventStart,
		);
		WaterfallMeasures.markMeasureEnd(
			WATERFALL_INITIAL_MEASURES.DOM_CONTENT_LOADED_EVENT,
			t.domContentLoadedEventEnd,
		);
	}

	// Server-timing
	if (t.serverTiming && t.serverTiming.length > 0) {
		for (const serverTiming of t.serverTiming) {
			const metricName = `${WATERFALL_INITIAL_MEASURES.SERVER_TIMING}/${serverTiming.name}`;
			const startTime = t.requestStart > 0 ? t.requestStart : 0;
			if (SERVER_TIMING_MEASURES.includes(serverTiming.name)) {
				// use requestStart as the start time, it's not the actual start time, but it's helpful
				// to align the server timing with the request start time for visualisation.
				WaterfallMeasures.markMeasureStart(metricName, startTime);
				WaterfallMeasures.markMeasureEnd(metricName, startTime + serverTiming.duration);
			}
		}
	}
}

/**
 * Collects `wf/` performance entries and pairs matching `.start` / `.end`
 * marks into timing objects.
 *
 * For each pair `wf/<key>.start` and `wf/<key>.end`, produces an entry
 * `{ [key]: { startTime, endTime } }`.
 *
 * Unpaired marks (a `.start` without a matching `.end` or vice versa) are
 * ignored.
 */
export function getWaterfallTimings(): Record<string, { startTime: number; endTime: number }> {
	const entries = getPerformanceEntries('wf/');
	const startTimes: Record<string, number> = {};
	const timings: Record<string, { startTime: number; endTime: number }> = {};

	for (const { name, startTime } of entries) {
		if (name.endsWith('.start')) {
			const key = name.slice(0, -'.start'.length);
			startTimes[key] = startTime;
		} else if (name.endsWith('.end')) {
			const key = name.slice(0, -'.end'.length);
			if (typeof startTimes[key] === 'number') {
				timings[key] = { startTime: startTimes[key], endTime: startTime };
			}
		}
	}

	return timings;
}
